/**
 * Content Analysis Page
 * 
 * Displays content islands, bridges, and standalone posts
 * with connectivity scores and semantic relationships
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Loader2, 
  Clock,
  AlertTriangle,
  Link2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useSite } from '../../hooks/useSite';
import { analysisApi, linksApi } from '../../lib/dashboard-api';
import type { 
  ContentAnalysisData,
  TopicCluster,
  SemanticRelationship,
  PostConnectivityScore,
  PostNeedingAttention,
  CriticalFinding,
  ContentAnalysisStatistics
} from '../../lib/dashboard-api';

// Consistent color palette for strength indicators
const STRENGTH_COLORS = {
  strong: '#00a32a',  // Green - 8.0+
  medium: '#dba617',  // Amber - 5.0-7.9
  weak: '#646970',    // Gray - 0-4.9
};

// Tier colors for connectivity scores
const TIER_COLORS = {
  'Content Hub': '#10b981',
  'Well Connected': '#3b82f6',
  'Emerging Connections': '#f59e0b',
  'Isolated': '#ef4444',
};

function getStrengthLevel(strength: number): 'strong' | 'medium' | 'weak' {
  if (strength >= 0.8) return 'strong';
  if (strength >= 0.5) return 'medium';
  return 'weak';
}

function getStrengthColor(strength: number): string {
  const level = getStrengthLevel(strength);
  return STRENGTH_COLORS[level];
}

// State for selected post (relationship explorer)
interface SelectedPost {
  postId: number;
  postTitle: string;
}

export default function ContentAnalysisPage() {
  const { currentSite } = useSite();
  const [searchParams] = useSearchParams();
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ContentAnalysisData | null>(null);
  const [selectedPost, setSelectedPost] = useState<SelectedPost | null>(null);
  
  // Check for historical analysis ID in URL
  const analysisIdParam = searchParams.get('analysisId');
  const viewingHistorical = !!analysisIdParam;

  // Fetch content analysis data
  useEffect(() => {
    async function fetchData() {
      if (!currentSite?.id) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const analysisId = analysisIdParam ? parseInt(analysisIdParam) : undefined;
        const result = await analysisApi.getContentAnalysisData(currentSite.id, analysisId);
        setData(result);
      } catch (err) {
        console.error('Failed to fetch content analysis data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load content analysis data');
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchData();
  }, [currentSite?.id, analysisIdParam]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#ffdc18] mx-auto mb-4" />
          <p className="text-white/70">Loading content analysis...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Error loading content analysis</span>
          </div>
          <p className="text-white/70 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  // No data state
  if (!data?.hasData) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-white mb-6">Content Analysis</h1>
        
        <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] p-8 text-center">
          <div className="text-6xl mb-4">🏝️</div>
          <h2 className="text-xl font-semibold text-white mb-2">
            No Analysis Results Available
          </h2>
          <p className="text-white/70 mb-6">
            Run a baseline analysis to see content islands, bridges, and standalone posts.
          </p>
          <a 
            href="/dashboard" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#ffdc18] text-black font-medium rounded-lg hover:bg-[#ffdc18]/90 transition-colors"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  const { 
    analysis, 
    topicClusters = [], 
    semanticRelationships = [],
    postsNeedingAttention = [],
    criticalFinding,
    connectivityScores = {},
    statistics
  } = data;

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Content Analysis</h1>
        
        {viewingHistorical && analysis?.createdAt && (
          <div className="flex items-center gap-2 text-white/60 text-sm">
            <Clock className="w-4 h-4" />
            <span>
              Viewing past analysis from {new Date(analysis.createdAt).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>

      {/* Statistics Grid */}
      {statistics && (
        <StatisticsGrid 
          statistics={statistics} 
          semanticRelationships={semanticRelationships}
        />
      )}

      {/* Instructions */}
      <div className="bg-[var(--bg-secondary)]/50 rounded-lg p-4 border border-[var(--bg-tertiary)]">
        <p className="text-white/70 text-sm">
          <span className="font-medium text-white">🖱️ How to explore:</span>{' '}
          Click any post card to explore its semantic relationships.{' '}
          Post titles with ↗️ can be clicked to edit (opens in new tab).{' '}
          Connected posts will be highlighted in green across all islands.
        </p>
      </div>

      {/* Critical Finding Alert */}
      {criticalFinding && (
        <CriticalFindingAlert finding={criticalFinding} />
      )}

      {/* Content Islands With SVG Bridges */}
      <IslandsWithBridges
        topicClusters={topicClusters}
        semanticRelationships={semanticRelationships}
        connectivityScores={connectivityScores}
        postsNeedingAttention={postsNeedingAttention}
        onSelectPost={setSelectedPost}
      />
      
      {/* Relationship Explorer Popup */}
      {selectedPost && (
        <RelationshipExplorerPopup 
          postId={selectedPost.postId}
          postTitle={selectedPost.postTitle}
          semanticRelationships={semanticRelationships}
          connectivityScores={connectivityScores}
          topicClusters={topicClusters}
          onClose={() => setSelectedPost(null)}
        />
      )}
    </div>
  );
}

// ============================================
// Sub-Components
// ============================================

interface StatisticsGridProps {
  statistics: ContentAnalysisStatistics;
  semanticRelationships: SemanticRelationship[];
}

function StatisticsGrid({ statistics, semanticRelationships }: StatisticsGridProps) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  
  // Calculate relationship type breakdown
  const relationshipBreakdown: Record<string, number> = {};
  semanticRelationships.forEach(rel => {
    const type = rel.relationship_type || 'unknown';
    relationshipBreakdown[type] = (relationshipBreakdown[type] || 0) + 1;
  });
  
  // Sort by count descending
  const sortedBreakdown = Object.entries(relationshipBreakdown)
    .sort(([,a], [,b]) => b - a);

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {/* Total Posts */}
        <StatCard label="Total Posts" value={statistics.totalPosts} />
        
        {/* Content Islands */}
        <StatCard label="Content Islands" value={statistics.contentIslands} />
        
        {/* Posts in Islands */}
        <StatCard 
          label="Posts in Islands" 
          value={statistics.postsInIslands}
          subLabel={statistics.postsRequiringAttention > 0 
            ? `${statistics.postsRequiringAttention} Requires Attention` 
            : undefined}
        />
        
        {/* Standalone Posts */}
        <StatCard label="Standalone Posts" value={statistics.standalonePosts} />
        
        {/* Coverage */}
        <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] p-4">
          <div className="text-white/60 text-xs mb-2">Content Island Coverage</div>
          <div className="h-2 bg-[var(--bg-tertiary)] rounded-full mb-2">
            <div 
              className="h-full bg-[#00a32a] rounded-full transition-all"
              style={{ width: `${statistics.coveragePercentage}%` }}
            />
          </div>
          <div className="text-2xl font-bold text-white">
            {statistics.coveragePercentage}%
          </div>
        </div>
        
        {/* Semantic Links */}
        <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] p-4 relative">
          <div className="text-white/60 text-xs mb-1">Semantic Links</div>
          <div className="text-2xl font-bold text-white mb-2">
            {statistics.semanticLinks}
          </div>
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded hover:bg-purple-500/30 transition-colors"
          >
            {showBreakdown ? 'Hide Details' : 'Show Details'}
          </button>
        </div>
        
        {/* Other Internal Links */}
        <StatCard label="Other Internal Links" value={statistics.otherInternalLinks} />
      </div>
      
      {/* Relationship Breakdown Popup */}
      {showBreakdown && sortedBreakdown.length > 0 && (
        <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] p-4">
          <div className="text-center text-white font-medium mb-4">
            Semantic Relationship Types Breakdown
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {sortedBreakdown.map(([type, count]) => (
              <div 
                key={type}
                className="bg-[var(--bg-primary)] rounded-lg p-3 text-center"
              >
                <div className="text-xl font-bold text-purple-400">{count}</div>
                <div className="text-xs text-white/60 capitalize">
                  {type.replace(/_/g, ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  subLabel?: string;
}

function StatCard({ label, value, subLabel }: StatCardProps) {
  return (
    <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] p-4">
      <div className="text-white/60 text-xs mb-1">{label}</div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {subLabel && (
        <div className="text-xs text-amber-400 mt-1">{subLabel}</div>
      )}
    </div>
  );
}

interface CriticalFindingAlertProps {
  finding: CriticalFinding;
}

function CriticalFindingAlert({ finding }: CriticalFindingAlertProps) {
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
      <h3 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />
        Critical Finding: No Internal Links Detected
      </h3>
      <p className="text-white/80 mb-2">
        <span className="font-medium">Impact:</span> {finding.critical_finding}
      </p>
      {finding.recommendation && (
        <p className="text-white/80 mb-4">
          <span className="font-medium">Recommendation:</span> {finding.recommendation}
        </p>
      )}
      <a 
        href="/links" 
        className="inline-flex items-center gap-2 px-4 py-2 bg-[#ffdc18] text-black font-medium rounded-lg hover:bg-[#ffdc18]/90 transition-colors"
      >
        <Link2 className="w-4 h-4" />
        View Link Recommendations
      </a>
    </div>
  );
}

// ============================================
// Islands With SVG Bridges System
// ============================================

interface BridgeData {
  island1: string;
  island2: string;
  connections: Array<{
    sourcePostId: number;
    targetPostId: number;
    sourceTitle: string;
    targetTitle: string;
    relationshipType: string;
    strength: number;
    explanation?: string;
  }>;
  averageStrength: number;
  connectionCount: number;
  relationshipTypes: string[];
}

interface IslandsWithBridgesProps {
  topicClusters: TopicCluster[];
  semanticRelationships: SemanticRelationship[];
  connectivityScores: Record<string, PostConnectivityScore>;
  postsNeedingAttention: PostNeedingAttention[];
  onSelectPost: (post: SelectedPost | null) => void;
}

function IslandsWithBridges({ 
  topicClusters, 
  semanticRelationships, 
  connectivityScores, 
  postsNeedingAttention,
  onSelectPost 
}: IslandsWithBridgesProps) {
  const [showBridges, setShowBridges] = useState(false);
  const [selectedBridge, setSelectedBridge] = useState<BridgeData | null>(null);
  const [bridgeUpdateKey, setBridgeUpdateKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  
  // Build post-to-island mapping
  const postToIslandMap = useCallback(() => {
    const map = new Map<number, string>();
    topicClusters.forEach(cluster => {
      const clusterPosts = cluster.cluster_posts || [];
      clusterPosts.forEach(postId => {
        map.set(postId, cluster.cluster_name);
      });
      if (cluster.hub_post) {
        map.set(cluster.hub_post, cluster.cluster_name);
      }
    });
    return map;
  }, [topicClusters]);
  
  // Analyze relationships to find cross-island bridges
  const bridges = useCallback((): BridgeData[] => {
    const islandMap = postToIslandMap();
    const bridgesMap = new Map<string, BridgeData>();
    
    semanticRelationships.forEach(rel => {
      const sourceIsland = islandMap.get(rel.source_post_id);
      const targetIsland = islandMap.get(rel.target_post_id);
      
      if (sourceIsland && targetIsland && sourceIsland !== targetIsland) {
        const key = [sourceIsland, targetIsland].sort().join('|');
        
        if (!bridgesMap.has(key)) {
          bridgesMap.set(key, {
            island1: sourceIsland < targetIsland ? sourceIsland : targetIsland,
            island2: sourceIsland < targetIsland ? targetIsland : sourceIsland,
            connections: [],
            averageStrength: 0,
            connectionCount: 0,
            relationshipTypes: []
          });
        }
        
        const bridge = bridgesMap.get(key)!;
        const strength = rel.relationship_strength || 0.5;
        
        const sourceScore = connectivityScores[String(rel.source_post_id)];
        const targetScore = connectivityScores[String(rel.target_post_id)];
        
        bridge.connections.push({
          sourcePostId: rel.source_post_id,
          targetPostId: rel.target_post_id,
          sourceTitle: sourceScore?.postTitle || rel.source_post_title || `Post #${rel.source_post_id}`,
          targetTitle: targetScore?.postTitle || rel.target_post_title || `Post #${rel.target_post_id}`,
          relationshipType: rel.relationship_type,
          strength: strength * 10,
          explanation: rel.relationship_explanation
        });
        
        if (!bridge.relationshipTypes.includes(rel.relationship_type)) {
          bridge.relationshipTypes.push(rel.relationship_type);
        }
      }
    });
    
    return Array.from(bridgesMap.values()).map(bridge => ({
      ...bridge,
      connectionCount: bridge.connections.length,
      averageStrength: bridge.connections.reduce((sum, c) => sum + c.strength, 0) / bridge.connections.length / 10
    })).sort((a, b) => b.connectionCount - a.connectionCount);
  }, [semanticRelationships, connectivityScores, postToIslandMap])();
  
  // Find island element by name
  const findIslandElement = useCallback((islandName: string): HTMLElement | null => {
    if (!containerRef.current) return null;
    const islands = containerRef.current.querySelectorAll('[data-island-name]');
    for (const island of islands) {
      if (island.getAttribute('data-island-name') === islandName) {
        const rect = island.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          return island as HTMLElement;
        }
      }
    }
    return null;
  }, []);
  
  // Get bridge color based on strength
  const getBridgeColor = (strength: number): string => {
    if (strength >= 0.8) return STRENGTH_COLORS.strong;
    if (strength >= 0.5) return STRENGTH_COLORS.medium;
    return STRENGTH_COLORS.weak;
  };
  
  // Calculate bridge thickness based on connection count
  const getBridgeThickness = (connectionCount: number): number => {
    const maxThickness = 12;
    const minThickness = 3;
    const maxConnections = 25;
    
    if (connectionCount <= 1) return minThickness;
    if (connectionCount >= maxConnections) return maxThickness;
    
    const logScale = Math.log(connectionCount) / Math.log(maxConnections);
    return Math.round(minThickness + (maxThickness - minThickness) * logScale);
  };
  
  // Draw bridges as SVG paths
  const drawBridges = useCallback(() => {
    if (!svgRef.current || !containerRef.current || !showBridges) return;
    
    const svg = svgRef.current;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    
    // Clear existing paths
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }
    
    // Set SVG dimensions to match container (with extra padding for overflow)
    svg.setAttribute('width', String(container.scrollWidth + 80));
    svg.setAttribute('height', String(container.scrollHeight + 80));
    
    bridges.forEach((bridge) => {
      const island1El = findIslandElement(bridge.island1);
      const island2El = findIslandElement(bridge.island2);
      
      if (!island1El || !island2El) return;
      
      // Get title elements for more precise positioning
      const title1 = island1El.querySelector('h3') || island1El;
      const title2 = island2El.querySelector('h3') || island2El;
      
      const rect1 = title1.getBoundingClientRect();
      const rect2 = title2.getBoundingClientRect();
      
      // Calculate positions relative to container (add 40px offset for SVG padding)
      const svgOffset = 40;
      const x1 = rect1.left + rect1.width / 2 - containerRect.left + container.scrollLeft + svgOffset;
      const y1 = rect1.top + rect1.height / 2 - containerRect.top + container.scrollTop + svgOffset;
      const x2 = rect2.left + rect2.width / 2 - containerRect.left + container.scrollLeft + svgOffset;
      const y2 = rect2.top - 10 - containerRect.top + container.scrollTop + svgOffset;
      
      // Calculate curve
      const dx = x2 - x1;
      const dy = y2 - y1;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const curvature = Math.min(distance * 0.4, 150);
      
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;
      const perpX = -dy / distance * curvature;
      const perpY = dx / distance * curvature;
      const controlX = midX + perpX;
      const controlY = midY + perpY;
      
      const bridgeColor = getBridgeColor(bridge.averageStrength);
      const thickness = getBridgeThickness(bridge.connectionCount);
      
      // Create group for this bridge
      const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      group.setAttribute('class', 'bridge-group');
      group.style.cursor = 'pointer';
      group.style.pointerEvents = 'all';
      
      // Create path
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      const pathData = `M ${x1} ${y1} Q ${controlX} ${controlY} ${x2} ${y2}`;
      path.setAttribute('d', pathData);
      path.setAttribute('stroke', bridgeColor);
      path.setAttribute('stroke-width', String(thickness));
      path.setAttribute('fill', 'none');
      path.setAttribute('opacity', '0.85');
      path.setAttribute('stroke-linecap', 'round');
      
      // Create count badge at endpoint
      const badgeX = x2;
      const badgeY = y2 - 10;
      
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', String(badgeX));
      circle.setAttribute('cy', String(badgeY));
      circle.setAttribute('r', '18');
      circle.setAttribute('fill', 'white');
      circle.setAttribute('stroke', bridgeColor);
      circle.setAttribute('stroke-width', '2');
      circle.setAttribute('opacity', '0.95');
      
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', String(badgeX));
      text.setAttribute('y', String(badgeY + 5));
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('font-size', '14');
      text.setAttribute('font-weight', 'bold');
      text.setAttribute('fill', bridgeColor);
      text.textContent = String(bridge.connectionCount);
      
      // Hover effects
      group.addEventListener('mouseenter', () => {
        path.setAttribute('opacity', '1');
        path.setAttribute('stroke-width', String(thickness + 2));
        circle.setAttribute('opacity', '1');
      });
      
      group.addEventListener('mouseleave', () => {
        path.setAttribute('opacity', '0.85');
        path.setAttribute('stroke-width', String(thickness));
        circle.setAttribute('opacity', '0.95');
      });
      
      // Click handler
      const bridgeRef = bridge;
      group.addEventListener('click', (e) => {
        e.stopPropagation();
        setSelectedBridge(bridgeRef);
      });
      
      group.appendChild(path);
      group.appendChild(circle);
      group.appendChild(text);
      svg.appendChild(group);
    });
  }, [showBridges, bridges, findIslandElement]);
  
  // Redraw bridges when needed
  useEffect(() => {
    if (showBridges) {
      // Use requestAnimationFrame for smooth rendering
      requestAnimationFrame(() => {
        drawBridges();
      });
    }
  }, [showBridges, drawBridges, bridgeUpdateKey]);
  
  // Handle resize
  useEffect(() => {
    if (!showBridges) return;
    
    const handleResize = () => {
      setBridgeUpdateKey(k => k + 1);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Also observe container size changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [showBridges]);
  
  // Callback for island expand/collapse to trigger bridge redraw
  const handleIslandToggle = useCallback(() => {
    if (showBridges) {
      setTimeout(() => {
        setBridgeUpdateKey(k => k + 1);
      }, 250); // Wait for animation
    }
  }, [showBridges]);

  return (
    <div className="space-y-4">
      {/* Bridge Controls */}
      <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] p-4">
        <h3 className="text-white font-semibold mb-3">🌉 Content Island Bridges</h3>
        
        <div className="bg-[var(--bg-primary)] border-l-4 border-[#00a32a] rounded-r-lg p-3 mb-4">
          <p className="text-white/90 font-medium mb-1">What Are Bridges Between Content Islands?</p>
          <p className="text-white/60 text-sm">
            Bridges show semantic connections between your content islands - posts that share related themes 
            but belong to different topic clusters. These connections reveal opportunities for strategic 
            internal linking, content cross-promotion, and identifying content gaps between related topics.
          </p>
        </div>
        
        <div className="flex flex-wrap items-center justify-between gap-4">
          <button
            onClick={() => setShowBridges(!showBridges)}
            className="px-4 py-2 bg-[var(--bg-tertiary)] text-white rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors"
          >
            {showBridges ? 'Hide Bridges' : `Show Bridges (${bridges.length})`}
          </button>
          
          <div className="flex items-center gap-4 text-sm text-white/60">
            <span className="font-medium">Bridge Colors:</span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STRENGTH_COLORS.strong }} />
              Strong (80%+)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STRENGTH_COLORS.medium }} />
              Medium (50-79%)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STRENGTH_COLORS.weak }} />
              Weak (&lt;50%)
            </span>
          </div>
        </div>
        
        {showBridges && bridges.length === 0 && (
          <div className="text-center py-6 text-white/60 mt-4">
            No cross-island bridges detected. All semantic relationships are within the same content islands.
          </div>
        )}
      </div>

      {/* Content Islands Header */}
      <div className="text-center py-4">
        <h2 className="text-2xl font-bold text-white mb-2">
          🏝️ Your Content Islands
        </h2>
        <p className="text-white/60">
          Explore your content ecosystem - each island represents a cluster of related posts
        </p>
      </div>

      {/* Island Strength Legend */}
      <IslandStrengthLegend />

      {/* Content Islands Grid with SVG Overlay */}
      <div 
        ref={containerRef}
        className="relative"
        style={{ overflow: 'visible', paddingTop: '40px', marginTop: '-40px' }}
      >
        {/* SVG Bridge Layer */}
        {showBridges && (
          <svg
            ref={svgRef}
            className="absolute pointer-events-none"
            style={{ 
              zIndex: 10, 
              overflow: 'visible',
              top: '-40px',
              left: '-40px',
              width: 'calc(100% + 80px)',
              height: 'calc(100% + 80px)'
            }}
          >
            {/* Bridges will be drawn here dynamically */}
          </svg>
        )}
        
        {/* Islands Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {topicClusters.map((cluster, index) => (
            <IslandCard 
              key={cluster.cluster_name || index}
              cluster={cluster}
              connectivityScores={connectivityScores}
              semanticRelationships={semanticRelationships}
              postsNeedingAttention={postsNeedingAttention}
              onSelectPost={onSelectPost}
              onToggle={handleIslandToggle}
            />
          ))}
        </div>
      </div>
      
      {/* Empty state for no islands */}
      {topicClusters.length === 0 && (
        <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] p-8 text-center">
          <p className="text-white/70">
            No content islands found in this analysis.
          </p>
        </div>
      )}
      
      {/* Bridge Details Popup */}
      {selectedBridge && (
        <BridgeDetailsPopup 
          bridge={selectedBridge} 
          onClose={() => setSelectedBridge(null)} 
        />
      )}
    </div>
  );
}

interface BridgeDetailsPopupProps {
  bridge: BridgeData;
  onClose: () => void;
}

function BridgeDetailsPopup({ bridge, onClose }: BridgeDetailsPopupProps) {
  // averageStrength is already 0-1 scale, no need to divide again
  const strengthColor = getStrengthColor(bridge.averageStrength);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div 
        className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] max-w-2xl w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-[var(--bg-tertiary)] flex items-center justify-between">
          <h3 className="text-white font-semibold">🌉 Bridge Connection</h3>
          <button 
            onClick={onClose}
            className="text-white/60 hover:text-white text-2xl leading-none"
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
          {/* Islands Visual */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl mb-1">🏝️</div>
              <div className="text-white font-medium">{bridge.island1}</div>
            </div>
            <div className="flex-1 max-w-[200px] relative">
              <div className="h-1 bg-gradient-to-r rounded-full" 
                style={{ background: `linear-gradient(to right, ${strengthColor}, ${strengthColor}80)` }} 
              />
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded text-white text-sm font-medium"
                style={{ backgroundColor: strengthColor }}
              >
                {Math.round(bridge.averageStrength * 100)}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-1">🏝️</div>
              <div className="text-white font-medium">{bridge.island2}</div>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-[var(--bg-primary)] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-white">{bridge.connections.length}</div>
              <div className="text-xs text-white/60">Connection{bridge.connections.length !== 1 ? 's' : ''}</div>
            </div>
            <div className="bg-[var(--bg-primary)] rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-white">{bridge.relationshipTypes.length}</div>
              <div className="text-xs text-white/60">Relationship Type{bridge.relationshipTypes.length !== 1 ? 's' : ''}</div>
            </div>
          </div>
          
          {/* Relationship Types */}
          <div className="mb-6">
            <div className="text-white/60 text-sm mb-2 font-medium">Relationship Types:</div>
            <div className="flex flex-wrap gap-2">
              {bridge.relationshipTypes.map(type => (
                <span 
                  key={type}
                  className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs capitalize"
                >
                  {type.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
          
          {/* Connections */}
          <div>
            <div className="text-white/60 text-sm mb-2 font-medium">Post-to-Post Connections:</div>
            <div className="space-y-3">
              {bridge.connections.slice(0, 5).map((conn, idx) => (
                <div key={idx} className="bg-[var(--bg-primary)] rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm mb-2">
                    <span className="text-white font-medium">{conn.sourceTitle}</span>
                    <span className="text-[#ffdc18]">↔</span>
                    <span className="text-white font-medium">{conn.targetTitle}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded text-xs capitalize">
                      {conn.relationshipType.replace(/_/g, ' ')}
                    </span>
                    {conn.explanation && (
                      <span className="text-xs text-white/50 italic truncate flex-1">
                        "{conn.explanation}"
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {bridge.connections.length > 5 && (
                <div className="text-center text-white/50 text-sm">
                  + {bridge.connections.length - 5} more connection{bridge.connections.length - 5 !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function IslandStrengthLegend() {
  return (
    <div className="bg-[var(--bg-secondary)]/50 border-l-4 border-[#00a32a] rounded-r-lg p-4">
      <p className="text-white font-medium mb-2">Content Island Strength (1-10 scale):</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STRENGTH_COLORS.strong }} />
          <span className="text-white">Strong (8.0+)</span>
          <span className="text-white/60">- High topic authority and content depth</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STRENGTH_COLORS.medium }} />
          <span className="text-white">Medium (5.0-7.9)</span>
          <span className="text-white/60">- Good topic coverage with room for growth</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STRENGTH_COLORS.weak }} />
          <span className="text-white">Weak (0-4.9)</span>
          <span className="text-white/60">- Emerging topics needing more content</span>
        </div>
      </div>
    </div>
  );
}

interface IslandCardProps {
  cluster: TopicCluster;
  connectivityScores: Record<string, PostConnectivityScore>;
  semanticRelationships: SemanticRelationship[];
  postsNeedingAttention: PostNeedingAttention[];
  onSelectPost: (post: SelectedPost | null) => void;
  onToggle?: () => void;
}

function IslandCard({ cluster, connectivityScores, semanticRelationships, postsNeedingAttention, onSelectPost, onToggle }: IslandCardProps) {
  const [showDescription, setShowDescription] = useState(false);
  const [showHubPost, setShowHubPost] = useState(false);
  const [showPosts, setShowPosts] = useState(false);
  
  const strength = cluster.cluster_strength || 0;
  const strengthColor = getStrengthColor(strength);
  const displayStrength = (strength * 10).toFixed(1);
  
  // Find hub post: first check connectivity scores for isHubPost flag, then fall back to cluster.hub_post
  const hubFromScores = Object.values(connectivityScores).find(
    score => score.isHubPost && score.hubCategory === cluster.cluster_name
  );
  const hubPostId = hubFromScores ? Number(hubFromScores.postId) : cluster.hub_post;
  
  // Calculate total posts in island
  const clusterPosts = cluster.cluster_posts || [];
  const totalPosts = new Set([hubPostId, ...clusterPosts].filter(Boolean)).size;
  
  // Count posts with semantic relationships
  const getPostRelationshipCount = (postId: number): number => {
    return semanticRelationships.filter(rel => 
      rel.source_post_id === postId || rel.target_post_id === postId
    ).length;
  };

  return (
    <div 
      className="bg-[var(--bg-secondary)] rounded-xl border-2 transition-all hover:shadow-lg"
      style={{ borderColor: `${strengthColor}30` }}
      data-island-name={cluster.cluster_name}
    >
      {/* Island Header */}
      <div className="p-4 border-b border-[var(--bg-tertiary)]">
        <div className="flex items-start gap-3">
          {/* Island Icon */}
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg flex-shrink-0"
            style={{ background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)` }}
          >
            🏝️
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-lg truncate">
              {cluster.cluster_name}
            </h3>
            <p className="text-white/60 text-sm">{totalPosts} posts</p>
          </div>
          
          {/* Strength Badge */}
          <div 
            className="px-3 py-1 rounded-full text-white text-sm font-medium flex-shrink-0"
            style={{ backgroundColor: strengthColor }}
          >
            {displayStrength}
          </div>
        </div>
        
        {/* Description Toggle */}
        {cluster.description && (
          <div className="mt-3">
            <button
              onClick={() => setShowDescription(!showDescription)}
              className="flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors"
            >
              {showDescription ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              {showDescription ? 'Hide Description' : 'Show Description'}
            </button>
            {showDescription && (
              <div className="mt-2 p-3 bg-purple-500/10 rounded-lg text-white/80 text-sm italic">
                {cluster.description}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Hub Post Toggle */}
      {hubPostId && (
        <div className="px-4 py-2 border-b border-[var(--bg-tertiary)]">
          <button
            onClick={() => {
              setShowHubPost(!showHubPost);
              onToggle?.();
            }}
            className="flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors w-full"
          >
            {showHubPost ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {showHubPost ? 'Hide Hub Post' : 'Show Hub Post'}
          </button>
          
          {showHubPost && (
            <div className="mt-2">
              <HubPostCard 
                postId={hubPostId}
                connectivityScore={connectivityScores[String(hubPostId)]}
                relationshipCount={getPostRelationshipCount(hubPostId)}
                allPostTitles={cluster.all_post_titles}
                onSelectPost={onSelectPost}
              />
            </div>
          )}
        </div>
      )}
      
      {/* Posts Toggle */}
      {clusterPosts.length > 0 && (
        <div className="px-4 py-2">
          <button
            onClick={() => {
              setShowPosts(!showPosts);
              onToggle?.();
            }}
            className="flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors w-full"
          >
            {showPosts ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {showPosts ? `Hide Posts (${clusterPosts.length})` : `Show Posts (${clusterPosts.length})`}
          </button>
          
          {showPosts && (
            <div className="mt-2 space-y-2">
              {clusterPosts
                .filter(postId => postId !== hubPostId)
                .map(postId => {
                  const excluded = postsNeedingAttention.find(
                    p => p.post_id === postId && (p.is_excluded || p.isExcluded)
                  );
                  
                  return (
                    <PostCard
                      key={postId}
                      postId={postId}
                      connectivityScore={connectivityScores[String(postId)]}
                      relationshipCount={getPostRelationshipCount(postId)}
                      allPostTitles={cluster.all_post_titles}
                      excluded={excluded}
                      onSelectPost={onSelectPost}
                    />
                  );
                })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface HubPostCardProps {
  postId: number;
  connectivityScore?: PostConnectivityScore;
  relationshipCount: number;
  allPostTitles?: Record<string, string>;
  onSelectPost: (post: SelectedPost | null) => void;
}

function HubPostCard({ postId, connectivityScore, relationshipCount, allPostTitles, onSelectPost }: HubPostCardProps) {
  const { currentSite } = useSite();
  const [showReason, setShowReason] = useState(false);
  
  const title = connectivityScore?.postTitle || allPostTitles?.[String(postId)] || `Post #${postId}`;
  const tier = connectivityScore?.tier || 'Isolated';
  const score = connectivityScore?.connectivityScore || 0;
  
  // Hub-specific data from v2.0
  const hubTarget = connectivityScore?.hubTargetLinks || 8;
  const currentLinks = connectivityScore?.inboundLinks || 0;
  const hubProgress = Math.min(100, (currentLinks / hubTarget) * 100);
  const linksNeeded = Math.max(0, hubTarget - currentLinks);
  const progressToContentHub = connectivityScore?.progressToContentHub || 0;
  const designationReason = connectivityScore?.hubDesignationReason;
  const designatedAt = connectivityScore?.hubDesignatedAt;
  
  // Calculate hub status from connections if not provided by API
  // This ensures the status matches the actual connection count
  const hubStatus = connectivityScore?.hubConnectivityStatus || (
    currentLinks >= hubTarget ? 'strong' :
    currentLinks >= Math.floor(hubTarget * 0.5) ? 'adequate' :
    'under_connected'
  );
  
  // Hub status colors
  const hubStatusStyles: Record<string, { bg: string; text: string; label: string }> = {
    under_connected: { bg: 'rgba(255,193,7,0.2)', text: '#ffc107', label: '⚠️ Needs Links' },
    adequate: { bg: 'rgba(0,188,212,0.2)', text: '#00bcd4', label: '✓ On Track' },
    strong: { bg: 'rgba(76,175,80,0.2)', text: '#4caf50', label: '✅ Strong' }
  };
  const statusStyle = hubStatusStyles[hubStatus] || hubStatusStyles.under_connected;
  
  // Build HubSpot editor URL
  const editorUrl = currentSite?.platformId 
    ? `https://app.hubspot.com/blog/${currentSite.platformId}/editor/${postId}/content`
    : null;

  return (
    <div 
      className="p-4 rounded-lg text-white"
      style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
    >
      {/* Header with Status Badge */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-white/80 uppercase tracking-wider flex items-center gap-2">
          🏛️ Hub Post
          {designatedAt && (
            <span className="text-white/60 normal-case">
              • Since {new Date(designatedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <div 
          className="px-2 py-0.5 rounded text-xs font-medium"
          style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
        >
          {statusStyle.label}
        </div>
      </div>
      
      {/* Title */}
      <div className="font-medium mb-3">
        {editorUrl ? (
          <a 
            href={editorUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:underline"
            onClick={e => e.stopPropagation()}
          >
            {title} <span className="text-sm">↗️</span>
          </a>
        ) : (
          title
        )}
      </div>
      
      {/* Hub Connection Progress */}
      <div className="mb-3">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-white/70">Hub Connections</span>
          <span className="text-[#ffdc18] font-medium">{currentLinks} / {hubTarget}</span>
        </div>
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full transition-all"
            style={{ 
              width: `${hubProgress}%`,
              background: hubProgress >= 100 
                ? 'linear-gradient(90deg, #4caf50, #8bc34a)' 
                : 'linear-gradient(90deg, #ffdc18, #ffa726)'
            }}
          />
        </div>
        {linksNeeded > 0 && (
          <div className="text-xs text-white/70 mt-1">
            {linksNeeded} more connection{linksNeeded !== 1 ? 's' : ''} to reach hub target
          </div>
        )}
      </div>
      
      {/* Content Hub Tier Progress (if not already at tier) */}
      {progressToContentHub > 0 && progressToContentHub < 100 && (
        <div className="mb-3 p-2 bg-white/10 rounded">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-white/70">Progress to Content Hub Tier</span>
            <span className="text-white/90">{Math.round(progressToContentHub)}%</span>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#ffdc18] rounded-full transition-all"
              style={{ width: `${progressToContentHub}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Score & Tier Badge */}
      <div className="flex items-center gap-2 mb-3">
        <div 
          className="px-2 py-0.5 rounded text-xs font-medium"
          style={{ backgroundColor: 'rgba(255,220,24,0.25)', color: '#ffdc18' }}
        >
          {score.toFixed(1)} • {tier}
        </div>
      </div>
      
      {/* Why This Is Hub (expandable) */}
      {designationReason && (
        <div className="mb-2">
          <button
            onClick={(e) => { e.stopPropagation(); setShowReason(!showReason); }}
            className="text-xs text-white/70 hover:text-white/90 flex items-center gap-1"
          >
            {showReason ? '▼' : '▶'} Why this is your Hub
          </button>
          {showReason && (
            <p className="mt-1 text-xs text-white/80 pl-3 border-l border-white/20">
              {designationReason}
            </p>
          )}
        </div>
      )}
      
      {/* View Connections */}
      {relationshipCount > 0 ? (
        <button 
          onClick={() => onSelectPost({ postId, postTitle: title })}
          className="text-sm text-white/90 hover:text-[#ffdc18] transition-colors cursor-pointer"
        >
          🔗 View {relationshipCount} Connections
        </button>
      ) : (
        <div className="text-sm text-white/60">
          No semantic connections
        </div>
      )}
    </div>
  );
}

interface PostCardProps {
  postId: number;
  connectivityScore?: PostConnectivityScore;
  relationshipCount: number;
  allPostTitles?: Record<string, string>;
  excluded?: PostNeedingAttention;
  onSelectPost: (post: SelectedPost | null) => void;
}

function PostCard({ postId, connectivityScore, relationshipCount, allPostTitles, excluded, onSelectPost }: PostCardProps) {
  const { currentSite } = useSite();
  const title = connectivityScore?.postTitle || allPostTitles?.[String(postId)] || `Post #${postId}`;
  const tier = connectivityScore?.tier || 'Isolated';
  const score = connectivityScore?.connectivityScore || 0;
  const tierColor = TIER_COLORS[tier as keyof typeof TIER_COLORS] || TIER_COLORS.Isolated;
  
  // Build HubSpot editor URL
  const editorUrl = currentSite?.platformId 
    ? `https://app.hubspot.com/blog/${currentSite.platformId}/editor/${postId}/content`
    : null;

  return (
    <div 
      className={`p-3 rounded-lg bg-[var(--bg-primary)] border-l-4 ${
        excluded ? 'border-amber-500' : 'border-purple-500'
      }`}
    >
      {/* Badges */}
      {excluded && (
        <div className="mb-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
            ⚠️ Excluded
          </span>
        </div>
      )}
      
      <div className="font-medium text-white text-sm">
        {editorUrl ? (
          <a 
            href={editorUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:underline hover:text-[#ffdc18] transition-colors"
            onClick={e => e.stopPropagation()}
          >
            {title} <span className="text-xs">↗️</span>
          </a>
        ) : (
          title
        )}
      </div>
      <div className="text-xs text-white/50 mt-1">Post ID: {postId}</div>
      
      {/* Health Indicators */}
      <div className="flex items-center gap-2 mt-2">
        <div 
          className="px-2 py-0.5 rounded text-xs font-medium"
          style={{ backgroundColor: `${tierColor}20`, color: tierColor }}
        >
          {score.toFixed(1)} • {tier}
        </div>
      </div>
      
      {/* Excluded Rationale */}
      {excluded && (excluded.exclude_reason || excluded.detailed_reasoning) && (
        <div className="mt-2 text-xs text-white/60">
          <span className="font-medium">Why:</span>{' '}
          {(excluded.exclude_reason || excluded.detailed_reasoning || '').slice(0, 100)}
          {((excluded.exclude_reason || excluded.detailed_reasoning || '').length > 100) && '...'}
        </div>
      )}
      
      {/* Relationship Indicator */}
      {!excluded && (
        relationshipCount > 0 ? (
          <button 
            onClick={() => onSelectPost({ postId, postTitle: title })}
            className="mt-2 text-xs text-purple-400 hover:text-[#ffdc18] transition-colors cursor-pointer"
          >
            🔗 View {relationshipCount} Connections
          </button>
        ) : (
          <div className="mt-2 text-xs text-white/40">
            No connections
          </div>
        )
      )}
    </div>
  );
}

// ============================================
// Relationship Explorer Popup
// ============================================

interface RelationshipExplorerPopupProps {
  postId: number;
  postTitle: string;
  semanticRelationships: SemanticRelationship[];
  connectivityScores: Record<string, PostConnectivityScore>;
  topicClusters: TopicCluster[];
  onClose: () => void;
}

function RelationshipExplorerPopup({ 
  postId, 
  postTitle, 
  semanticRelationships, 
  connectivityScores,
  topicClusters,
  onClose 
}: RelationshipExplorerPopupProps) {
  const { currentSite } = useSite();
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  
  // Fetch pending recommendation count for this post
  useEffect(() => {
    if (!currentSite?.id) return;
    
    linksApi.getRecommendations(currentSite.id, {
      postId: String(postId),
      status: 'pending',
      limit: 1  // We only need the count from stats
    })
      .then(res => {
        setPendingCount(res.stats?.pending || 0);
      })
      .catch(() => {
        setPendingCount(0);
      });
  }, [currentSite?.id, postId]);
  
  // Build post-to-island mapping
  const postToIslandMap = new Map<number, string>();
  topicClusters.forEach(cluster => {
    const clusterPosts = cluster.cluster_posts || [];
    clusterPosts.forEach(pid => {
      postToIslandMap.set(pid, cluster.cluster_name);
    });
    if (cluster.hub_post) {
      postToIslandMap.set(cluster.hub_post, cluster.cluster_name);
    }
  });
  
  // Find all relationships involving this post
  const outboundRelationships = semanticRelationships.filter(rel => rel.source_post_id === postId);
  const inboundRelationships = semanticRelationships.filter(rel => rel.target_post_id === postId);
  
  // Get post's island
  const postIsland = postToIslandMap.get(postId);
  
  // Get post connectivity info
  const postScore = connectivityScores[String(postId)];
  const tier = postScore?.tier || 'Isolated';
  const score = postScore?.connectivityScore || 0;
  const tierColor = TIER_COLORS[tier as keyof typeof TIER_COLORS] || TIER_COLORS.Isolated;
  
  // Build HubSpot editor URL
  const editorUrl = currentSite?.platformId 
    ? `https://app.hubspot.com/blog/${currentSite.platformId}/editor/${postId}/content`
    : null;
  
  // Group relationships by type
  const groupedOutbound: Record<string, Array<{ rel: SemanticRelationship; score?: PostConnectivityScore; island?: string }>> = {};
  outboundRelationships.forEach(rel => {
    const type = rel.relationship_type || 'general';
    if (!groupedOutbound[type]) groupedOutbound[type] = [];
    groupedOutbound[type].push({
      rel,
      score: connectivityScores[String(rel.target_post_id)],
      island: postToIslandMap.get(rel.target_post_id)
    });
  });
  
  const groupedInbound: Record<string, Array<{ rel: SemanticRelationship; score?: PostConnectivityScore; island?: string }>> = {};
  inboundRelationships.forEach(rel => {
    const type = rel.relationship_type || 'general';
    if (!groupedInbound[type]) groupedInbound[type] = [];
    groupedInbound[type].push({
      rel,
      score: connectivityScores[String(rel.source_post_id)],
      island: postToIslandMap.get(rel.source_post_id)
    });
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div 
        className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-[var(--bg-tertiary)] flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg">🔗 Semantic Relationships</h3>
              <div className="text-white/60 text-sm mt-1">
                {editorUrl ? (
                  <a 
                    href={editorUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#ffdc18] hover:underline"
                  >
                    {postTitle} ↗️
                  </a>
                ) : (
                  postTitle
                )}
              </div>
            </div>
            <button 
              onClick={onClose}
              className="text-white/60 hover:text-white text-2xl leading-none ml-4"
            >
              ×
            </button>
          </div>
          
          {/* Post Stats Bar */}
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <div 
              className="px-2 py-1 rounded text-xs font-medium"
              style={{ backgroundColor: `${tierColor}30`, color: tierColor }}
            >
              {score.toFixed(1)} • {tier}
            </div>
            {postIsland && (
              <div className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs">
                🏝️ {postIsland}
              </div>
            )}
            <div className="text-xs text-white/60">
              {outboundRelationships.length} outbound • {inboundRelationships.length} inbound
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Outbound Relationships */}
          {outboundRelationships.length > 0 && (
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <span className="text-green-400">→</span> 
                Outbound Links ({outboundRelationships.length})
                <span className="text-white/50 text-xs font-normal">
                  This post links to:
                </span>
              </h4>
              
              {Object.entries(groupedOutbound).map(([type, items]) => (
                <div key={type} className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-300 rounded text-xs capitalize">
                      {type.replace(/_/g, ' ')}
                    </span>
                    <span className="text-white/40 text-xs">({items.length})</span>
                  </div>
                  <div className="space-y-2 pl-2 border-l-2 border-green-500/30">
                    {items.map(({ rel, score: relScore, island }) => (
                      <RelatedPostItem 
                        key={`${rel.source_post_id}-${rel.target_post_id}`}
                        postId={rel.target_post_id}
                        title={relScore?.postTitle || rel.target_post_title || `Post #${rel.target_post_id}`}
                        score={relScore}
                        island={island}
                        explanation={rel.relationship_explanation}
                        direction="outbound"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Inbound Relationships */}
          {inboundRelationships.length > 0 && (
            <div className="mb-6">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <span className="text-blue-400">←</span> 
                Inbound Links ({inboundRelationships.length})
                <span className="text-white/50 text-xs font-normal">
                  Posts linking to this:
                </span>
              </h4>
              
              {Object.entries(groupedInbound).map(([type, items]) => (
                <div key={type} className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded text-xs capitalize">
                      {type.replace(/_/g, ' ')}
                    </span>
                    <span className="text-white/40 text-xs">({items.length})</span>
                  </div>
                  <div className="space-y-2 pl-2 border-l-2 border-blue-500/30">
                    {items.map(({ rel, score: relScore, island }) => (
                      <RelatedPostItem 
                        key={`${rel.source_post_id}-${rel.target_post_id}`}
                        postId={rel.source_post_id}
                        title={relScore?.postTitle || rel.source_post_title || `Post #${rel.source_post_id}`}
                        score={relScore}
                        island={island}
                        explanation={rel.relationship_explanation}
                        direction="inbound"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* No relationships */}
          {outboundRelationships.length === 0 && inboundRelationships.length === 0 && (
            <div className="text-center py-8 text-white/60">
              No semantic relationships found for this post.
            </div>
          )}
        </div>
        
        {/* Footer - only show if there are pending recommendations */}
        {pendingCount !== null && pendingCount > 0 && (
          <div className="p-4 border-t border-[var(--bg-tertiary)] flex-shrink-0">
            <a 
              href={`/links?postId=${postId}&postTitle=${encodeURIComponent(postTitle)}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#ffdc18] text-black font-medium rounded-lg hover:bg-[#ffdc18]/90 transition-colors"
            >
              <Link2 className="w-4 h-4" />
              View {pendingCount} Pending Recommendation{pendingCount !== 1 ? 's' : ''}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

interface RelatedPostItemProps {
  postId: number;
  title: string;
  score?: PostConnectivityScore;
  island?: string;
  explanation?: string;
  direction: 'inbound' | 'outbound';
}

function RelatedPostItem({ postId, title, score, island, explanation, direction }: RelatedPostItemProps) {
  const { currentSite } = useSite();
  
  const tier = score?.tier || 'Isolated';
  const connectivityScore = score?.connectivityScore || 0;
  const tierColor = TIER_COLORS[tier as keyof typeof TIER_COLORS] || TIER_COLORS.Isolated;
  
  const editorUrl = currentSite?.platformId 
    ? `https://app.hubspot.com/blog/${currentSite.platformId}/editor/${postId}/content`
    : null;
  
  const borderColor = direction === 'outbound' ? 'border-green-500/30' : 'border-blue-500/30';

  return (
    <div className={`bg-[var(--bg-primary)] rounded-lg p-3 border-l-2 ${borderColor}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="font-medium text-white text-sm">
          {editorUrl ? (
            <a 
              href={editorUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-[#ffdc18] transition-colors"
            >
              {title} <span className="text-xs opacity-70">↗️</span>
            </a>
          ) : (
            title
          )}
        </div>
        <div 
          className="px-1.5 py-0.5 rounded text-xs"
          style={{ backgroundColor: `${tierColor}20`, color: tierColor }}
        >
          {connectivityScore.toFixed(1)}
        </div>
      </div>
      
      <div className="flex items-center gap-2 text-xs">
        <span className="text-white/50">ID: {postId}</span>
        {island && (
          <>
            <span className="text-white/30">•</span>
            <span className="text-purple-400">🏝️ {island}</span>
          </>
        )}
      </div>
      
      {explanation && (
        <div className="mt-2 text-xs text-white/50 italic">
          "{explanation.slice(0, 150)}{explanation.length > 150 ? '...' : ''}"
        </div>
      )}
    </div>
  );
}
